import React, { useState, useEffect, useCallback } from 'react';
import { useApi } from '../../../hooks/useApi';
import { fmtDateTime } from '../../../utils/ruDates';

const BASE = '/api/v1/admin/jev';

const TABS = [
  { key: 'settings', label: 'Ключ и настройки' },
  { key: 'compare', label: 'Сравнение с биддером' },
  { key: 'stats', label: 'Сводка' },
  { key: 'reference', label: 'Эталон' },
  { key: 'calls', label: 'Журнал' },
];

const LINE_LABELS = {
  BUYOUTS: ['Выкупы по дням', 'error', 'naive_error', 'наивный («как на прошлой неделе»)'],
  DEMAND_PROFIT: ['Прибыль по дню спроса', 'error', 'accrual_error', 'бухгалтерский день'],
  CART_LAG: ['Корзины → заказы', 'error', 'same_day_error', '«всё в тот же день»'],
  RELEASE: ['Выброс накопленных корзин', 'error', null, null],
  ORDER_FORECAST: ['Прогноз заказов на неделю', 'error', 'naive_error', 'наивный (неделю назад)'],
};
const VERDICT = {
  PASS: ['норма', '#065f46', 'rgba(209,250,229,0.6)'],
  FAIL: ['не прошёл', '#991b1b', 'rgba(254,226,226,0.6)'],
  NO_DATA: ['нет данных', '#666', 'transparent'],
};

const DIR_LABELS = { UP: '↑ вверх', DOWN: '↓ вниз', HOLD: '— держать' };
const STATUS_COLORS = {
  OK: { bg: '#dcfce7', fg: '#166534' },
  ERROR: { bg: '#fee2e2', fg: '#991b1b' },
  SKIPPED: { bg: '#f3f4f6', fg: '#374151' },
};

const inputStyle = { padding: '8px 10px', borderRadius: 6, border: '1px solid #ddd' };
const th = { padding: 10, textAlign: 'left' };
const td = { padding: 10, fontSize: 13, verticalAlign: 'top' };

function Card({ children, style }) {
  return (
    <div style={{ background: 'var(--surface, white)', borderRadius: 12, boxShadow: '0 4px 6px rgba(0,0,0,0.05)', padding: 24, ...style }}>
      {children}
    </div>
  );
}

function Badge({ status }) {
  const c = STATUS_COLORS[status] || STATUS_COLORS.SKIPPED;
  return <span style={{ padding: '3px 8px', borderRadius: 4, fontSize: 12, background: c.bg, color: c.fg }}>{status}</span>;
}

function pct(value) {
  return value == null ? '—' : `${Math.round(value * 100)}%`;
}

function Probabilities({ probabilities }) {
  if (!probabilities || typeof probabilities !== 'object') return null;
  return (
    <div style={{ fontSize: 12, color: '#666' }}>
      {Object.entries(probabilities).map(([k, v]) => `${k} ${pct(v)}`).join(' · ')}
    </div>
  );
}

function ComparisonTable({ rows }) {
  if (!rows || rows.length === 0) return <div style={{ color: '#666', fontSize: 13 }}>Сравнивать нечего</div>;
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <thead style={{ background: '#f8f9fa', borderBottom: '2px solid #eee' }}>
        <tr>
          <th style={th}>Решение</th>
          <th style={th}>Биддер</th>
          <th style={th}>Jev</th>
          <th style={th}>Совпало</th>
        </tr>
      </thead>
      <tbody>
        {rows.map(r => (
          <tr key={r.question} style={{ borderBottom: '1px solid #f5f5f5', background: r.agree === false ? 'rgba(254,226,226,0.35)' : undefined }}>
            <td style={td}>{r.label}</td>
            <td style={td}>
              <div style={{ fontWeight: 600 }}>{DIR_LABELS[r.bidder] || r.bidder}</div>
              <div style={{ fontSize: 12, color: '#666', maxWidth: 360 }}>{r.bidder_reason}</div>
            </td>
            <td style={td}>
              <div style={{ fontWeight: 600 }}>{DIR_LABELS[r.jev] || r.jev || '—'}</div>
              <Probabilities probabilities={r.probabilities} />
              {r.confidence != null && <div style={{ fontSize: 12, color: '#666' }}>уверенность {pct(r.confidence)}</div>}
            </td>
            <td style={td}>{r.agree == null ? '—' : r.agree ? 'да' : 'нет'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function JsonBlock({ title, value }) {
  return (
    <details style={{ marginTop: 10 }}>
      <summary style={{ cursor: 'pointer', fontSize: 13 }}>{title}</summary>
      <pre style={{ fontSize: 12, maxHeight: 360, overflow: 'auto', background: 'var(--glass-55, #f8f9fa)', padding: 10, borderRadius: 6 }}>
        {JSON.stringify(value, null, 2)}
      </pre>
    </details>
  );
}

function MapTree({ rows, blocks }) {
  const nodes = (rows || []).filter(r => r.decision === false);
  if (nodes.length === 0) return null;
  const byBlock = {};
  nodes.forEach(r => { (byBlock[r.block] = byBlock[r.block] || []).push(r); });
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div style={{ fontWeight: 600, marginTop: 8 }}>Карта размышлений</div>
      {Object.keys(blocks || {}).concat(Object.keys(byBlock)).filter((b, i, all) => byBlock[b] && all.indexOf(b) === i).map(block => {
        const items = byBlock[block];
        const coded = items.filter(r => r.agree != null);
        const agreed = coded.filter(r => r.agree).length;
        return (
          <details key={block} open={coded.some(r => r.agree === false)}>
            <summary style={{ cursor: 'pointer', fontSize: 13 }}>
              <b>{block}. {(blocks || {})[block] || ''}</b>
              <span style={{ color: '#666' }}> · {items.length} узл.{coded.length ? ` · с кодом совпало ${agreed} из ${coded.length}` : ''}</span>
            </summary>
            <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 4 }}>
              <tbody>
                {items.map(r => (
                  <tr key={r.question} style={{ borderBottom: '1px solid #f5f5f5', background: r.agree === false ? 'rgba(254,226,226,0.35)' : undefined }}>
                    <td style={{ ...td, width: '32%' }}>{r.label}</td>
                    <td style={td}>
                      <div>{r.jev_label || '—'}</div>
                      {r.confidence != null && <div style={{ fontSize: 12, color: '#666' }}>уверенность {pct(r.confidence)}</div>}
                    </td>
                    <td style={{ ...td, color: '#666', width: '24%' }}>{r.coded_label ? `код: ${r.coded_label}` : ''}</td>
                    <td style={{ ...td, width: 40 }}>{r.agree == null ? '' : r.agree ? '✓' : '✗'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </details>
        );
      })}
    </div>
  );
}

function ProbeTable({ rows }) {
  const moved = (rows || []).filter(r => r.changed === true).length;
  const known = (rows || []).filter(r => r.changed != null).length;
  const order = (rows || []).some(r => r.kind === 'order');
  return (
    <div>
      <div style={{ fontSize: 13, color: '#666', margin: '6px 0' }}>
        {order
          ? 'Вся карта заново на подменённой ситуации: курс должен попасть в допустимые.'
          : `Сдвинулось ${moved} из ${known} узлов. Узел, который не сдвинулся на заведомо другие данные, это поле не читает.`}
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead style={{ background: '#f8f9fa', borderBottom: '2px solid #eee' }}>
          <tr><th style={th}>Узел</th><th style={th}>Было</th><th style={th}>С подменой</th><th style={th}>{order ? 'Допустим' : 'Сдвинулся'}</th></tr>
        </thead>
        <tbody>
          {(rows || []).map(r => (
            <tr key={r.question} style={{ borderBottom: '1px solid #f5f5f5', background: r.changed === false ? 'rgba(254,226,226,0.35)' : undefined }}>
              <td style={td}><span style={{ color: '#666' }}>{r.block}.</span> {r.label}</td>
              <td style={td}>{r.before_label || '—'}</td>
              <td style={td}>{r.jev_label || '—'}</td>
              <td style={td}>{r.changed == null ? '—' : r.changed ? 'да' : 'нет'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ProbePanel({ call }) {
  const { request, showStatus } = useApi();
  const [probes, setProbes] = useState([]);
  const [selected, setSelected] = useState('');
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    request(`${BASE}/probes`)
      .then(d => { setProbes(d.items || []); if (d.items?.length) setSelected(d.items[0].key); })
      .catch(() => {});
  }, [request]); // eslint-disable-line

  const run = async () => {
    setRunning(true);
    setResult(null);
    try {
      setResult(await request(`${BASE}/calls/${call.id}/probe/${selected}`, { method: 'POST' }));
    } catch (e) {
      showStatus('error', 'Ошибка: ' + e.message);
    } finally {
      setRunning(false);
    }
  };

  return (
    <div style={{ marginTop: 8, padding: 12, border: '1px dashed #ddd', borderRadius: 8 }}>
      <div style={{ fontWeight: 600, marginBottom: 6 }}>Проверка чувствительности</div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <select value={selected} onChange={e => setSelected(e.target.value)} style={{ ...inputStyle, padding: '6px 10px' }}>
          {probes.map(p => <option key={p.key} value={p.key}>{p.label}{p.kind === 'order' ? '' : ` (блоки ${p.blocks.join(', ')})`}</option>)}
        </select>
        <button className="btn btn--ghost btn--sm" onClick={run} disabled={running || !selected}>
          {running ? 'Спрашиваем...' : 'Подменить и спросить'}
        </button>
      </div>
      {result && (
        <div style={{ marginTop: 8 }}>
          {result.error && <div style={{ color: '#991b1b', fontSize: 13 }}>{result.error}</div>}
          <ProbeTable rows={result.comparison} />
        </div>
      )}
    </div>
  );
}

const KNOB_LABELS = {
  plan_level: ['План трафика', { MUCH_LOWER: 'сильно ниже', LOWER: 'ниже', KEEP: 'как есть', HIGHER: 'выше', MUCH_HIGHER: 'сильно выше' }],
  bid: ['Ставка', { BOTH: 'в обе стороны', UP_ONLY: 'только вверх', DOWN_ONLY: 'только вниз', FREEZE: 'заморожена' }],
  price: ['Цена', { BOTH: 'в обе стороны', UP_ONLY: 'только вверх', DOWN_ONLY: 'только вниз', FREEZE: 'заморожена' }],
  lever: ['Главный рычаг', { BID: 'ставка', PRICE: 'цена', NONE: 'нет' }],
  aggressiveness: ['Шаги', { CAUTIOUS: 'осторожно', NORMAL: 'обычно', BOLD: 'смело' }],
};

function OrderCard({ order, rows }) {
  const course = (rows || []).find(r => r.question === 'o_course');
  if (!order) return null;
  return (
    <div style={{ padding: 12, borderRadius: 8, background: 'var(--glass-55, #f0f7ff)', border: '1px solid #dbeafe' }}>
      <div style={{ fontWeight: 600, marginBottom: 4 }}>Приказ (пока только в журнал)</div>
      <div style={{ fontSize: 14, marginBottom: 8 }}>
        Курс: <b>{order.course_label}</b>
        {course?.confidence != null && <span style={{ fontSize: 12, color: '#666' }}> · уверенность {pct(course.confidence)}</span>}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 8, fontSize: 13 }}>
        {Object.entries(KNOB_LABELS).map(([key, [label, values]]) => (
          <div key={key}>
            <div style={{ color: '#666', fontSize: 12 }}>{label}</div>
            <div style={{ fontWeight: 600 }}>{values[order[key]] || order[key] || '—'}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ChangesList({ changes }) {
  if (!Array.isArray(changes) || changes.length === 0) return null;
  return (
    <details>
      <summary style={{ cursor: 'pointer', fontSize: 13 }}>Лента изменений с прошлого приказа ({changes.length})</summary>
      <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 4 }}>
        <tbody>
          {changes.map((e, i) => (
            <tr key={i} style={{ borderBottom: '1px solid #f5f5f5' }}>
              <td style={{ ...td, whiteSpace: 'nowrap' }}>{e.when}</td>
              <td style={td}>{e.what}{e.sku ? ` · SKU ${e.sku}` : ''}{e.offer ? ` · ${e.offer}` : ''}</td>
              <td style={td}>{e.from ?? '—'} → {e.to ?? '—'}</td>
              <td style={{ ...td, color: '#666' }}>{e.by}{e.verdict ? ` · итог: ${e.verdict}` : ''}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </details>
  );
}

function CallDetail({ call }) {
  if (call.source === 'PROBE') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ fontSize: 13 }}><Badge status={call.status} /> Проба: {(call.questions || {}).probe_label || ''}</div>
        {call.error && <div style={{ color: '#991b1b', fontSize: 13 }}>{call.error}</div>}
        <ProbeTable rows={call.comparison} />
      </div>
    );
  }
  const decisions = (call.comparison || []).filter(r => r.decision !== false);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', fontSize: 13, flexWrap: 'wrap' }}>
        <Badge status={call.status} />
        <span>{call.automation_name || call.automation_id}</span>
        <span style={{ color: '#666' }}>{call.model}</span>
        {call.latency_ms != null && <span style={{ color: '#666' }}>{Math.round(call.latency_ms)} мс</span>}
        {call.input_tokens != null && <span style={{ color: '#666' }}>{call.input_tokens} ток.</span>}
      </div>
      {call.error && <div style={{ color: '#991b1b', fontSize: 13 }}>{call.error}</div>}
      {((call.questions || {}).escalation || []).length > 0 && (
        <div style={{ padding: '8px 12px', background: '#fef9c3', color: '#854d0e', borderRadius: 6, fontSize: 13 }}>
          <b>Доклад скрипта:</b> {call.questions.escalation.join('; ')}
        </div>
      )}
      <OrderCard order={(call.questions || {}).order} rows={call.comparison} />
      <ChangesList changes={(call.state?.tactical || {}).changes} />
      <ComparisonTable rows={decisions} />
      <MapTree rows={call.comparison} blocks={call.blocks} />
      {call.status === 'OK' && call.state && <ProbePanel call={call} />}
      {call.state && <JsonBlock title="Что видел Jev (state)" value={call.state} />}
      {call.questions && <JsonBlock title="Вопросы" value={call.questions} />}
      {call.response && <JsonBlock title="Сырой ответ" value={call.response} />}
    </div>
  );
}

// ── Settings ─────────────────────────────────────────────────────────────

function SettingsTab() {
  const { request, showStatus } = useApi();
  const [cfg, setCfg] = useState(null);
  const [newKey, setNewKey] = useState('');
  const [saving, setSaving] = useState(false);
  const [ping, setPing] = useState(null);
  const [pinging, setPinging] = useState(false);

  useEffect(() => {
    request(`${BASE}/settings`).then(setCfg).catch(() => showStatus('error', 'Ошибка загрузки настроек'));
  }, [request]); // eslint-disable-line

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        base_url: cfg.base_url,
        model: cfg.model,
        timeout_s: Number(cfg.timeout_s),
        shadow_enabled: cfg.shadow_enabled,
        execute_orders: cfg.execute_orders,
        shadow_interval_minutes: Number(cfg.shadow_interval_minutes),
        chunk_size: Number(cfg.chunk_size),
      };
      if (newKey.trim()) payload.api_key = newKey.trim();
      setCfg(await request(`${BASE}/settings`, { method: 'PUT', body: JSON.stringify(payload) }));
      setNewKey('');
      showStatus('success', 'Настройки сохранены');
    } catch (e) {
      showStatus('error', 'Ошибка: ' + e.message);
    } finally {
      setSaving(false);
    }
  };

  const doPing = async () => {
    setPinging(true);
    setPing(null);
    try {
      setPing(await request(`${BASE}/ping`, { method: 'POST' }));
    } catch (e) {
      setPing({ error: e.message });
    } finally {
      setPinging(false);
    }
  };

  if (!cfg) return <Card>Загрузка...</Card>;

  return (
    <Card style={{ maxWidth: 620, display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div>
        <div style={{ fontWeight: 600, marginBottom: 6 }}>API-ключ</div>
        <div style={{ fontSize: 13, color: cfg.api_key_set ? '#166534' : '#991b1b', marginBottom: 8 }}>
          {cfg.api_key_set ? `Задан: ${cfg.api_key_masked}` : 'Не задан'}
        </div>
        <input
          type="password"
          placeholder="Вставьте новый ключ, чтобы заменить"
          value={newKey}
          onChange={e => setNewKey(e.target.value)}
          style={{ ...inputStyle, width: '100%', boxSizing: 'border-box' }}
        />
      </div>

      <div>
        <div style={{ fontWeight: 600, marginBottom: 6 }}>Адрес API</div>
        <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>
          Timeweb AI-прокси: https://api.timeweb.ai, напрямую TypeSafe: https://api.typesafe.ai. Запрос уходит на /v1/systemone
        </div>
        <input value={cfg.base_url} onChange={e => setCfg({ ...cfg, base_url: e.target.value })} style={{ ...inputStyle, width: '100%', boxSizing: 'border-box' }} />
      </div>

      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>Модель</div>
          <input value={cfg.model} onChange={e => setCfg({ ...cfg, model: e.target.value })} style={{ ...inputStyle, width: 180 }} />
        </div>
        <div>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>Таймаут, с</div>
          <input type="number" min="1" max="60" step="1" value={cfg.timeout_s} onChange={e => setCfg({ ...cfg, timeout_s: e.target.value })} style={{ ...inputStyle, width: 100 }} />
        </div>
        <div>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>Вопросов за запрос</div>
          <input type="number" min="1" max="60" step="1" value={cfg.chunk_size} onChange={e => setCfg({ ...cfg, chunk_size: e.target.value })} style={{ ...inputStyle, width: 100 }} />
        </div>
      </div>
      <div style={{ fontSize: 12, color: '#666', marginTop: -12 }}>
        Карта — около 65 вопросов в две ступени. Первая ступень делится на запросы по столько вопросов и идёт параллельно.
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
        <div>
          <div style={{ fontWeight: 600 }}>Jev включён</div>
          <div style={{ fontSize: 12, color: '#666' }}>
            Раз в сутки (после 09:00 МСК) Jev ставит группам курс тактики, в течение дня поправляет его
            по плановым проходам и докладам скрипта. Всё пишется в журнал.
          </div>
        </div>
        <input type="checkbox" checked={!!cfg.shadow_enabled} onChange={e => setCfg({ ...cfg, shadow_enabled: e.target.checked })} style={{ width: 20, height: 20, flexShrink: 0 }} />
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16,
        padding: 12, borderRadius: 8, border: cfg.execute_orders ? '1px solid #fca5a5' : '1px solid #eee' }}>
        <div>
          <div style={{ fontWeight: 600 }}>Исполнять приказы</div>
          <div style={{ fontSize: 12, color: '#666' }}>
            Общий рубильник. Выключено — Jev только советует везде. Включено — курс Jev исполняют группы, где
            пользователь выбрал «Тактика: Jev» (в настройках группы); остальные работают скриптом. Ошибка Jev — скрипт сам.
            Пользователи видят курс и рекомендации на вкладке «Тактика».
          </div>
        </div>
        <input type="checkbox" checked={!!cfg.execute_orders} disabled={!cfg.shadow_enabled}
          onChange={e => setCfg({ ...cfg, execute_orders: e.target.checked })} style={{ width: 20, height: 20, flexShrink: 0 }} />
      </div>

      <div>
        <div style={{ fontWeight: 600, marginBottom: 6 }}>Интервал тени по группе, мин</div>
        <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>
          Не чаще одного прохода карты на группу за этот интервал (30–1440). Главный регулятор расхода токенов.
        </div>
        <input type="number" min="30" max="1440" step="30" value={cfg.shadow_interval_minutes} onChange={e => setCfg({ ...cfg, shadow_interval_minutes: e.target.value })} style={{ ...inputStyle, width: 120 }} />
      </div>

      <div style={{ display: 'flex', gap: 8 }}>
        <button className="btn btn--primary btn--sm" onClick={save} disabled={saving}>{saving ? 'Сохранение...' : 'Сохранить'}</button>
        <button className="btn btn--ghost btn--sm" onClick={doPing} disabled={pinging || !cfg.api_key_set}>{pinging ? 'Проверка...' : 'Проверить ключ'}</button>
      </div>

      {ping && (ping.error
        ? <div style={{ padding: '8px 12px', background: '#fee2e2', color: '#991b1b', borderRadius: 6, fontSize: 13 }}>{ping.error}</div>
        : <div style={{ padding: '8px 12px', background: '#dcfce7', color: '#166534', borderRadius: 6, fontSize: 13 }}>
            Работает: {ping.model}, {Math.round(ping.latency_ms)} мс. Тестовый вопрос «срочно ли?» → {pct(ping.answers?.is_urgent?.noul)}
          </div>)}
    </Card>
  );
}

// ── Manual comparison ────────────────────────────────────────────────────

function CompareTab() {
  const { request, showStatus } = useApi();
  const [automations, setAutomations] = useState([]);
  const [selected, setSelected] = useState('');
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    request(`${BASE}/automations`)
      .then(d => { setAutomations(d.items || []); if (d.items?.length) setSelected(d.items[0].id); })
      .catch(() => showStatus('error', 'Ошибка загрузки групп биддера'));
  }, [request]); // eslint-disable-line

  const run = async () => {
    setRunning(true);
    setResult(null);
    try {
      setResult(await request(`${BASE}/compare/${selected}`, { method: 'POST' }));
    } catch (e) {
      showStatus('error', 'Ошибка: ' + e.message);
    } finally {
      setRunning(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Card>
        <div style={{ fontSize: 13, color: '#666', marginBottom: 12 }}>
          Тактика считает план на текущий момент (ничего не пишет в кабинет), Jev получает те же данные без решения тактики.
          Результат сохраняется в журнал.
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <select value={selected} onChange={e => setSelected(e.target.value)} style={{ ...inputStyle, minWidth: 320, maxWidth: '100%' }}>
            {automations.length === 0 && <option value="">Групп биддера нет</option>}
            {automations.map(a => (
              <option key={a.id} value={a.id}>
                {a.name} · {a.account_name} · {a.strategy_type} · {a.status}{!a.bid && !a.price ? ' · без ставки и цены' : ''}
              </option>
            ))}
          </select>
          <button className="btn btn--primary btn--sm" onClick={run} disabled={running || !selected}>
            {running ? 'Считаем...' : 'Прогнать сравнение'}
          </button>
        </div>
      </Card>
      {result && <Card><CallDetail call={result} /></Card>}
    </div>
  );
}

// ── Stats ────────────────────────────────────────────────────────────────

function Matrix({ matrix }) {
  const dirs = ['UP', 'HOLD', 'DOWN'];
  return (
    <table style={{ borderCollapse: 'collapse', fontSize: 13 }}>
      <thead>
        <tr>
          <th style={{ ...th, fontSize: 12, color: '#666' }}>биддер ↓ / Jev →</th>
          {dirs.map(d => <th key={d} style={{ ...th, textAlign: 'center' }}>{DIR_LABELS[d]}</th>)}
        </tr>
      </thead>
      <tbody>
        {dirs.map(b => (
          <tr key={b} style={{ borderTop: '1px solid #eee' }}>
            <td style={{ ...td, fontWeight: 600 }}>{DIR_LABELS[b]}</td>
            {dirs.map(j => (
              <td key={j} style={{ ...td, textAlign: 'center', background: b === j ? 'rgba(220,252,231,0.5)' : undefined }}>
                {matrix?.[b]?.[j] || 0}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function StatsTab() {
  const { request, showStatus } = useApi();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    request(`${BASE}/stats`).then(setStats).catch(() => showStatus('error', 'Ошибка загрузки сводки'));
  }, [request]); // eslint-disable-line

  if (!stats) return <Card>Загрузка...</Card>;
  const levers = [['bid', 'Ставка группы'], ['price', 'Цена модели']];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Card>
        <div style={{ display: 'flex', gap: 32, flexWrap: 'wrap', fontSize: 13 }}>
          <div><div style={{ color: '#666' }}>Успешных вызовов</div><div style={{ fontSize: 22, fontWeight: 700 }}>{stats.calls}</div></div>
          {Object.entries(stats.by_status || {}).map(([k, v]) => (
            <div key={k}><div style={{ color: '#666' }}>{k}</div><div style={{ fontSize: 22, fontWeight: 700 }}>{v}</div></div>
          ))}
          <div><div style={{ color: '#666' }}>Задержка p50 / p95</div><div style={{ fontSize: 22, fontWeight: 700 }}>
            {stats.latency_p50_ms != null ? `${Math.round(stats.latency_p50_ms)} / ${Math.round(stats.latency_p95_ms)} мс` : '—'}
          </div></div>
        </div>
      </Card>

      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
        {levers.map(([key, label]) => {
          const s = stats.levers?.[key];
          return (
            <Card key={key} style={{ flex: '1 1 320px' }}>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>{label}</div>
              <div style={{ fontSize: 13, color: '#666', marginBottom: 12 }}>
                {s ? `Совпадение ${pct(s.rate)} (${s.agreed} из ${s.compared})` : 'Данных пока нет'}
              </div>
              {s && <Matrix matrix={s.matrix} />}
            </Card>
          );
        })}
      </div>

      {stats.outcomes && (
        <Card>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>Исход курсов</div>
          <div style={{ fontSize: 12, color: '#666', marginBottom: 12 }}>{stats.outcomes.note}</div>
          {(stats.outcomes.by_course || []).length === 0 ? <div style={{ fontSize: 13, color: '#666' }}>
            Суточных курсов пока нет — первый появится после 09:00 МСК.</div> : (
            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 12 }}>
              <thead style={{ background: '#f8f9fa', borderBottom: '2px solid #eee' }}>
                <tr><th style={th}>Курс</th><th style={th}>Оценено</th><th style={th}>Цель улучшилась</th><th style={th}>Медиана изменения</th><th style={th}>Ждут 3 дня</th></tr>
              </thead>
              <tbody>
                {stats.outcomes.by_course.map(c => (
                  <tr key={c.course} style={{ borderBottom: '1px solid #f5f5f5' }}>
                    <td style={td}>{c.label || c.course}</td>
                    <td style={td}>{c.done}</td>
                    <td style={td}>{c.done ? `${pct(c.improved_share)} (${c.improved} из ${c.done})` : '—'}</td>
                    <td style={td}>{c.median_change_pct == null ? '—' : `${c.median_change_pct > 0 ? '+' : ''}${c.median_change_pct}%`}</td>
                    <td style={td}>{c.pending}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {(stats.outcomes.items || []).length > 0 && (
            <details>
              <summary style={{ cursor: 'pointer', fontSize: 13 }}>Последние курсы ({stats.outcomes.items.length})</summary>
              <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 4 }}>
                <tbody>
                  {stats.outcomes.items.map((i, n) => (
                    <tr key={n} style={{ borderBottom: '1px solid #f5f5f5', background: i.improved === false ? 'rgba(254,226,226,0.35)' : undefined }}>
                      <td style={td}>{i.date}</td>
                      <td style={td}>{i.name || i.automation_id}</td>
                      <td style={td}>{i.course_label}</td>
                      <td style={td}>{i.status === 'DONE' ? `${i.before} → ${i.after}` : 'ждёт 3 полных дня'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </details>
          )}
        </Card>
      )}

      {(stats.nodes || []).length > 0 && (
        <Card>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>По узлам карты</div>
          <div style={{ fontSize: 12, color: '#666', marginBottom: 12 }}>
            «С кодом» — узлы, где у тактики есть свой порог; там видно, где суждение Jev расходится с кодом.
            Распределение ответов показывает, куда Jev склоняется в узлах без аналога в коде.
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 640 }}>
              <thead style={{ background: '#f8f9fa', borderBottom: '2px solid #eee' }}>
                <tr><th style={th}>Узел</th><th style={th}>Ответов</th><th style={th}>Различает</th><th style={th}>С кодом совпало</th><th style={th}>Ответы Jev</th></tr>
              </thead>
              <tbody>
                {stats.nodes.map(n => (
                  <tr key={n.question} style={{ borderBottom: '1px solid #f5f5f5', background: n.insensitive || (n.rate != null && n.rate < 0.5) ? 'rgba(254,226,226,0.35)' : undefined }}>
                    <td style={td}><span style={{ color: '#666' }}>{n.block}.</span> {n.label}</td>
                    <td style={td}>{n.answered} <span style={{ color: '#666', fontSize: 12 }}>({n.groups} гр.)</span></td>
                    <td style={td}>
                      {n.insensitive
                        ? <span style={{ color: '#991b1b' }}>нет — {pct(n.top_share)} одно и то же</span>
                        : n.top_share != null ? `частый ответ ${pct(n.top_share)}` : '—'}
                    </td>
                    <td style={td}>{n.coded ? `${pct(n.rate)} (${n.agreed} из ${n.coded})` : '—'}</td>
                    <td style={{ ...td, fontSize: 12, color: '#666' }}>
                      {Object.entries(n.answers || {}).sort((a, b) => b[1] - a[1]).map(([k, v]) => `${k}: ${v}`).join(' · ')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <Card>
        <div style={{ fontWeight: 600, marginBottom: 12 }}>По группам биддера</div>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead style={{ background: '#f8f9fa', borderBottom: '2px solid #eee' }}>
            <tr><th style={th}>Группа</th><th style={th}>Вызовов</th><th style={th}>Решений</th><th style={th}>Совпало</th></tr>
          </thead>
          <tbody>
            {(stats.automations || []).length === 0 && <tr><td colSpan="4" style={{ padding: 30, textAlign: 'center' }}>Пока пусто</td></tr>}
            {(stats.automations || []).map(a => (
              <tr key={a.automation_id || 'none'} style={{ borderBottom: '1px solid #f5f5f5' }}>
                <td style={td}>{a.name || a.automation_id || 'удалена'}</td>
                <td style={td}>{a.calls}</td>
                <td style={td}>{a.compared}</td>
                <td style={td}>{pct(a.rate)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

// ── Journal ──────────────────────────────────────────────────────────────

function CallsTab() {
  const { request, showStatus } = useApi();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState('');
  const [source, setSource] = useState('');
  const [disagree, setDisagree] = useState(false);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(null);
  const limit = 50;

  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
    if (status) params.set('status', status);
    if (source) params.set('source', source);
    if (disagree) params.set('disagree_only', 'true');
    request(`${BASE}/calls?${params.toString()}`)
      .then(d => { setItems(d.items || []); setTotal(d.total || 0); })
      .catch(() => showStatus('error', 'Ошибка загрузки журнала'))
      .finally(() => setLoading(false));
  }, [request, status, source, disagree, offset]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  const toggle = async (id) => {
    if (open?.id === id) { setOpen(null); return; }
    try {
      setOpen(await request(`${BASE}/calls/${id}`));
    } catch (e) {
      showStatus('error', 'Ошибка: ' + e.message);
    }
  };

  return (
    <Card>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
        <select value={status} onChange={e => { setStatus(e.target.value); setOffset(0); }} style={{ ...inputStyle, padding: '6px 10px' }}>
          <option value="">Все статусы</option>
          <option value="OK">OK</option>
          <option value="ERROR">ERROR</option>
          <option value="SKIPPED">SKIPPED</option>
        </select>
        <select value={source} onChange={e => { setSource(e.target.value); setOffset(0); }} style={{ ...inputStyle, padding: '6px 10px' }}>
          <option value="">Все источники</option>
          <option value="MANUAL">Вручную</option>
          <option value="SHADOW">Теневой режим</option>
          <option value="ESCALATE">Доклады скрипта</option>
          <option value="PROBE">Пробы чувствительности</option>
        </select>
        <label style={{ fontSize: 13, display: 'flex', gap: 6, alignItems: 'center' }}>
          <input type="checkbox" checked={disagree} onChange={e => { setDisagree(e.target.checked); setOffset(0); }} />
          Только расхождения
        </label>
        <span style={{ fontSize: 13, color: '#666' }}>{total} вызовов</span>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 760 }}>
          <thead style={{ background: '#f8f9fa', borderBottom: '2px solid #eee' }}>
            <tr>
              <th style={th}>Дата</th>
              <th style={th}>Группа</th>
              <th style={th}>Источник</th>
              <th style={th}>Статус</th>
              <th style={th}>Совпало</th>
              <th style={th}>Задержка</th>
              <th style={th}>Ошибка / пропуск</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan="7" style={{ padding: 30, textAlign: 'center' }}>Загрузка...</td></tr>}
            {!loading && items.length === 0 && <tr><td colSpan="7" style={{ padding: 30, textAlign: 'center' }}>Вызовов нет</td></tr>}
            {items.map(c => (
              <React.Fragment key={c.id}>
                <tr onClick={() => toggle(c.id)} style={{ borderBottom: '1px solid #f5f5f5', cursor: 'pointer' }}>
                  <td style={td}>{fmtDateTime(c.created_at)}</td>
                  <td style={td}>{c.automation_name || c.automation_id || '—'}</td>
                  <td style={td}>{{ SHADOW: 'план', ESCALATE: 'доклад', PROBE: 'проба' }[c.source] || 'вручную'}</td>
                  <td style={td}><Badge status={c.status} /></td>
                  <td style={td}>{c.compared ? `${c.agreed} из ${c.compared}` : '—'}</td>
                  <td style={td}>{c.latency_ms != null ? `${Math.round(c.latency_ms)} мс` : '—'}</td>
                  <td style={{ ...td, fontSize: 12, color: '#666', maxWidth: 280 }}>{c.error}</td>
                </tr>
                {open?.id === c.id && (
                  <tr><td colSpan="7" style={{ padding: 16, background: 'var(--glass-55, #fafafa)' }}><CallDetail call={open} /></td></tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
        <button className="btn btn--ghost btn--xs" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - limit))}>← Назад</button>
        <button className="btn btn--ghost btn--xs" disabled={offset + limit >= total} onClick={() => setOffset(offset + limit)}>Далее →</button>
      </div>
    </Card>
  );
}

function ReferenceTab() {
  const { request, showStatus } = useApi();
  const [backtest, setBacktest] = useState(null);
  const [suite, setSuite] = useState(null);

  const load = useCallback(() => {
    request(`${BASE}/demand-backtest`).then(setBacktest).catch(() => showStatus('error', 'Ошибка загрузки замера'));
    request(`${BASE}/probe-suite`).then(setSuite).catch(() => {});
  }, [request]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  const start = async (path, done) => {
    try {
      await request(`${BASE}/${path}`, { method: 'POST' });
      showStatus('success', done);
      setTimeout(load, 3000);
    } catch (e) {
      showStatus('error', 'Ошибка: ' + e.message);
    }
  };
  const share = v => v == null ? '—' : `${Math.round(v * 1000) / 10}%`;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
          <div>
            <div style={{ fontWeight: 600 }}>Отложенный спрос: замер на истории</div>
            <div style={{ fontSize: 12, color: '#666', maxWidth: 720 }}>
              На каждой контрольной дате модель считается только по тому, что было известно в тот день, и сравнивается
              с тем, что случилось потом. Зелёная строка — модель точнее простой альтернативы и укладывается в порог.
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn--ghost btn--sm" onClick={load}>Обновить</button>
            <button className="btn btn--primary btn--sm" disabled={backtest?.running}
              onClick={() => start('demand-backtest', 'Замер запущен, займёт несколько минут')}>
              {backtest?.running ? 'Идёт замер...' : 'Запустить замер'}</button>
          </div>
        </div>
      </Card>
      {(backtest?.items || []).map(item => (
        <Card key={item.account_name}>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>
            {item.account_name} <span style={{ fontSize: 12, color: item.passed ? '#065f46' : '#991b1b' }}>
              {item.passed ? '· всё в норме' : '· есть отклонения'}</span>
            <span style={{ fontSize: 12, color: '#666', fontWeight: 400 }}> · на {item.as_of}, корзины: {item.source === 'STATS' ? 'вся воронка' : item.source === 'ADS' ? 'реклама' : 'нет'}</span>
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead style={{ background: '#f8f9fa', borderBottom: '2px solid #eee' }}>
              <tr><th style={th}>Что меряем</th><th style={th}>Ошибка модели</th><th style={th}>Ошибка альтернативы</th><th style={th}>Точек</th><th style={th}>Порог</th><th style={th}>Итог</th></tr>
            </thead>
            <tbody>
              {Object.entries(item.lines || {}).map(([key, line]) => {
                const [label, err, alt, altLabel] = LINE_LABELS[key] || [key, 'error'];
                const [verdict, color, bg] = VERDICT[line.status] || VERDICT.NO_DATA;
                return (
                  <tr key={key} style={{ borderBottom: '1px solid #f5f5f5', background: bg }}>
                    <td style={td}>{label}</td>
                    <td style={td}>{share(line[err])}</td>
                    <td style={td}>{alt ? `${share(line[alt])} — ${altLabel}` : '—'}</td>
                    <td style={td}>{line.points ?? line.events ?? '—'}</td>
                    <td style={{ ...td, color: '#666' }}>{line.threshold}</td>
                    <td style={{ ...td, color, fontWeight: 600 }}>{verdict}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      ))}
      {backtest && !(backtest.items || []).length && <Card>Замеров ещё не было — запустите.</Card>}

      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
          <div>
            <div style={{ fontWeight: 600 }}>Пробы карты с ожидаемым ответом</div>
            <div style={{ fontSize: 12, color: '#666', maxWidth: 720 }}>
              В состояние подставляется заведомая ситуация (волны нет, волна куплена, прибыль раздута, корзины накоплены
              и витрина упала), и узел должен ответить ожидаемо. Норма — от {share(suite?.threshold)} верных ответов.
            </div>
          </div>
          <button className="btn btn--primary btn--sm" disabled={suite?.running}
            onClick={() => start('probe-suite', 'Пробы запущены')}>{suite?.running ? 'Идут пробы...' : 'Запустить пробы'}</button>
        </div>
        {suite?.pass_rate != null && (
          <div style={{ fontSize: 14, margin: '8px 0', fontWeight: 600,
            color: suite.pass_rate >= suite.threshold ? '#065f46' : '#991b1b' }}>
            Верных ответов: {share(suite.pass_rate)}
          </div>
        )}
        {(suite?.items || []).length > 0 && (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead style={{ background: '#f8f9fa', borderBottom: '2px solid #eee' }}>
              <tr><th style={th}>Ситуация</th><th style={th}>Прогонов</th><th style={th}>Верно</th><th style={th}>Ответы</th></tr>
            </thead>
            <tbody>
              {suite.items.map(item => (
                <tr key={item.key} style={{ borderBottom: '1px solid #f5f5f5' }}>
                  <td style={td}>{item.label}</td>
                  <td style={td}>{item.runs}</td>
                  <td style={td}>{share(item.rate)}</td>
                  <td style={{ ...td, color: '#666' }}>{Object.entries(item.answers || {}).map(([a, n]) => `${a}: ${n}`).join('; ')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}

export default function AdminJev() {
  const [tab, setTab] = useState('settings');
  return (
    <div className="admin-page">
      <div style={{ marginBottom: 24 }}>
        <h1>Jev</h1>
        <p style={{ color: '#666' }}>Модель решений TypeSafe Jev рядом с тактикой OZON-биддера: ключ, ручные и теневые сравнения, журнал вызовов</p>
      </div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
        {TABS.map(t => (
          <button key={t.key} className={`btn btn--sm ${tab === t.key ? 'btn--primary' : 'btn--ghost'}`} onClick={() => setTab(t.key)}>
            {t.label}
          </button>
        ))}
      </div>
      {tab === 'settings' && <SettingsTab />}
      {tab === 'compare' && <CompareTab />}
      {tab === 'stats' && <StatsTab />}
      {tab === 'reference' && <ReferenceTab />}
      {tab === 'calls' && <CallsTab />}
    </div>
  );
}
